import React from "react";
import {
  Pill,
  Settings,
  MoreHorizontal,
  Calendar,
  Clock,
  CreditCard,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Subscription,
  getDaysAgo,
  getDaysUntil,
  getStatusColor,
  getStatusLabel,
  getEffectiveStatus,
} from "@/lib/subscriptionsData";

interface SubscriptionApprovalRowProps {
  subscription: Subscription;
  isDarkMode: boolean;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onProcess: () => void;
}

export const SubscriptionApprovalRow = React.memo(
  function SubscriptionApprovalRow({
    subscription,
    isDarkMode,
    isSelected,
    onSelect,
    onProcess,
  }: SubscriptionApprovalRowProps) {
    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    const handleMenuAction = (action: () => void) => {
      action();
      setDropdownOpen(false);
    };

    const lastSupplyDays = getDaysAgo(subscription.lastSupplyDate);
    const nextSupplyDays = getDaysUntil(subscription.nextSupplyDate);
    const effectiveStatus = getEffectiveStatus(subscription);
    const statusColor = getStatusColor(effectiveStatus);
    const statusLabel = getStatusLabel(effectiveStatus);

    const getNextSupplyIcon = () => {
      // If consultation is required, show different icon
      if (effectiveStatus === "consultation_required") {
        return <Settings className="w-4 h-4 text-blue-500 mr-1" />;
      }
      if (subscription.status === "overdue" || nextSupplyDays < 0) {
        return <AlertTriangle className="w-4 h-4 text-red-500 mr-1" />;
      } else if (nextSupplyDays <= 7) {
        return <Clock className="w-4 h-4 text-yellow-500 mr-1" />;
      }
      return <Calendar className="w-4 h-4 text-gray-500 mr-1" />;
    };

    const getStatusIcon = () => {
      switch (effectiveStatus) {
        case "awaiting_payment":
          return <CreditCard className="w-4 h-4 text-yellow-600 mr-1" />;
        case "overdue":
          return <AlertTriangle className="w-4 h-4 text-red-600 mr-1" />;
        case "supplied":
          return <CheckCircle className="w-4 h-4 text-green-600 mr-1" />;
        case "consultation_required":
          return <Settings className="w-4 h-4 text-blue-600 mr-1" />;
        default:
          return null;
      }
    };

    return (
      <tr className={`hover:${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}>
        <td className="px-3 py-4 whitespace-nowrap">
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => onSelect(!!checked)}
            aria-label={`Select subscription for ${subscription.patientName}`}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div
            className={`text-sm font-medium ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {subscription.patientName}
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center">
            <Pill className="w-4 h-4 text-blue-500 mr-2" />
            <div>
              <div
                className={`text-sm font-medium ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {subscription.medication}
              </div>
              <div
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {subscription.dosage}
              </div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div
            className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {subscription.prescribedBy}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div
              className={`text-sm font-medium ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {subscription.currentIssue}/{subscription.totalIssues}
            </div>
            <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{
                  width: `${
                    (subscription.currentIssue / subscription.totalIssues) * 100
                  }%`,
                }}
              />
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div
            className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {lastSupplyDays === 0
              ? "Today"
              : `${lastSupplyDays} day${lastSupplyDays === 1 ? "" : "s"} ago`}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            {getNextSupplyIcon()}
            <div
              className={`text-sm ${
                effectiveStatus === "consultation_required"
                  ? "text-blue-600 font-medium"
                  : nextSupplyDays < 0
                  ? "text-red-600 font-medium"
                  : nextSupplyDays <= 7
                  ? "text-yellow-600 font-medium"
                  : isDarkMode
                  ? "text-white"
                  : "text-gray-900"
              }`}
            >
              {effectiveStatus === "consultation_required"
                ? "Consultation needed"
                : nextSupplyDays < 0
                ? `${Math.abs(nextSupplyDays)} day${
                    Math.abs(nextSupplyDays) === 1 ? "" : "s"
                  } overdue`
                : nextSupplyDays === 0
                ? "Due today"
                : `In ${nextSupplyDays} day${nextSupplyDays === 1 ? "" : "s"}`}
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div
            className={`text-sm font-medium ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            £{subscription.pricePerIssue.toFixed(2)}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            {getStatusIcon()}
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor}`}
            >
              {statusLabel}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={onProcess}
              className="bg-blue-600 hover:bg-blue-700 h-7 px-2 text-xs"
            >
              <Settings className="w-3 h-3 mr-1" />
              Manage
            </Button>

            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  aria-label="More actions"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(!dropdownOpen);
                  }}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuAction(onProcess);
                  }}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Manage subscription
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuAction(() => {
                     
                    });
                  }}
                >
                  <Pill className="mr-2 h-4 w-4" />
                  Issue next supply
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuAction(() => {
                    
                    });
                  }}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  View payment details
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuAction(() => {
                     
                    });
                  }}
                  className="text-red-600"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Pause subscription
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </td>
      </tr>
    );
  }
);
